import { Progress, Tooltip } from '@mantine/core';
import { IconAlertTriangle, IconAlertCircle, IconChevronRight } from '@tabler/icons-react';
import Link from 'next/link';
import { api } from '~/trpc-extra/api';
import { useAuthData } from '~/hooks/auth/use-auth-data';
import { useProjectId } from '~/hooks/context/useProjectId';

type UsageLimitIndicatorProps = {
  isCollapsed?: boolean;
};

export function UsageLimitIndicator({ isCollapsed = false }: UsageLimitIndicatorProps) {
  const { isSuperAdmin } = useAuthData();
  const projectId = useProjectId();
  // Use skipExpensiveIfNoBlockingConfig to avoid ClickHouse/Stripe calls when no limit is configured
  const { data: status } = api.usageLimit.getStatus.useQuery(
    { skipExpensiveIfNoBlockingConfig: true },
    {
      enabled: !isSuperAdmin,
      refetchOnWindowFocus: true,
      staleTime: 5 * 60 * 1000,
    },
  );

  // Always link to usage tab for the recovery action
  const usageUrl = projectId ? `/project/${projectId}/settings?tab=usage` : '/settings?tab=usage';

  // Don't show for superAdmins or if no status
  if (isSuperAdmin || !status) {
    return null;
  }

  // Only show warning/blocked states when a limit that can actually block is configured
  // A config can block if: on-demand is disabled OR there's an on-demand cap
  // If on-demand is enabled with no cap, they have unlimited usage and won't be blocked
  const canConfigBlock = status.config && (!status.config.onDemandEnabled || status.config.onDemandCapCents !== null);
  if (!canConfigBlock) {
    return null;
  }

  // Calculate percentage for warning threshold
  // Use on-demand percentage if there's a cap, otherwise use included percentage
  const percentage = status.onDemandPercentUsed ?? status.includedPercentUsed;
  const { isBlocked } = status;
  const isWarning = percentage >= 80 && !isBlocked;

  // Only show if warning (80%+) or blocked
  if (!isWarning && !isBlocked) {
    return null;
  }

  return (
    <IndicatorContent
      isCollapsed={isCollapsed}
      isBlocked={isBlocked}
      percentage={percentage}
      usageUrl={usageUrl}
    />
  );
}

type IndicatorContentProps = {
  isCollapsed: boolean;
  isBlocked: boolean;
  percentage: number;
  usageUrl: string;
};

function IndicatorContent({ isCollapsed, isBlocked, percentage, usageUrl }: IndicatorContentProps) {
  const Icon = isBlocked ? IconAlertCircle : IconAlertTriangle;
  const label = isBlocked ? 'Limit reached' : 'Spending limit';
  const progressColor = isBlocked ? 'red' : 'yellow';

  if (isCollapsed) {
    return (
      <Tooltip
        label={
          <div className="text-center">
            <div className="font-medium">{isBlocked ? 'Spending limit reached' : 'Approaching spending limit'}</div>
            <div className="text-xs opacity-80">
              {isBlocked ? 'Click to adjust' : `${Math.round(percentage)}% used`}
            </div>
          </div>
        }
        position="right"
        withArrow
      >
        <Link
          href={usageUrl}
          className="mb-2 flex justify-center"
        >
          <div
            className={`rounded-md p-1.5 transition-colors ${
              isBlocked ?
                'bg-red-100 text-red-600 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-400 dark:hover:bg-red-900/50'
              : 'bg-yellow-100 text-yellow-600 hover:bg-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-400 dark:hover:bg-yellow-900/50'
            }`}
          >
            <Icon
              size={18}
              stroke={1.5}
            />
          </div>
        </Link>
      </Tooltip>
    );
  }

  return (
    <Link
      href={usageUrl}
      className={`group mx-2 mb-2 block rounded-md px-3 py-2.5 transition-colors ${
        isBlocked ?
          'bg-red-50 hover:bg-red-100 dark:bg-red-900/20 dark:hover:bg-red-900/30'
        : 'bg-yellow-50 hover:bg-yellow-100 dark:bg-yellow-900/20 dark:hover:bg-yellow-900/30'
      }`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon
            size={14}
            stroke={1.5}
            className={isBlocked ? 'text-red-500 dark:text-red-400' : 'text-yellow-600 dark:text-yellow-400'}
          />
          <span
            className={`text-xs font-medium ${
              isBlocked ? 'text-red-700 dark:text-red-300' : 'text-yellow-700 dark:text-yellow-300'
            }`}
          >
            {label}
          </span>
        </div>
        <IconChevronRight
          size={14}
          stroke={2}
          className={`transition-transform group-hover:translate-x-0.5 ${
            isBlocked ? 'text-red-400 dark:text-red-500' : 'text-yellow-500 dark:text-yellow-500'
          }`}
        />
      </div>
      <div className="mt-2 flex items-center gap-2">
        <Progress
          value={Math.min(percentage, 100)}
          size={6}
          color={progressColor}
          className="flex-1"
        />
        <span
          className={`text-[10px] font-medium tabular-nums ${
            isBlocked ? 'text-red-600 dark:text-red-400' : 'text-yellow-600 dark:text-yellow-400'
          }`}
        >
          {Math.round(percentage)}%
        </span>
      </div>
    </Link>
  );
}
